import React, { useCallback, useEffect, useMemo, useState } from "react";
import { bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { Redirect } from "react-router-dom";
import { push, replace } from "react-router-redux";

import {
    Box,
    Button,
    Col,
    Container,
    Grid,
    PageHeader,
    ToggleButtonGroup,
    transfersTestIds,
} from "@ui-kit/components/index.es";

import { resizableRoute } from "pages/_components/Resizable";

import { actions as transferActions, selectors as transferSelectors } from "reducers/transfer";
import { actions as templateActions } from "reducers/template";
import { actions as layoutActions } from "reducers/layout";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as statusSelectors } from "reducers/status";

import { isTransferHistoricV3Enabled, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import {
    getTransferExecutePath,
    getTransferExecuteTypeFromPath,
    getTransferHistoricPath,
    TRANSFER_EXECUTE_V2_PREFIX,
    TRANSFER_TICKET_V3_PATH,
    TRANSFER_CONFIRMATION_V3_PATH,
} from "util/v3UxConfig";
import resolveDraftTransactionListGoBack from "util/draftTransactionListGoBack";
import { getTransferInternalTitleI18nKey } from "util/transferInternalTitleLabels";
import {
    getTransferExecuteLabels,
    buildTransferExecuteTemplatesHeaderOption,
} from "v3/pages/transfers/transferExecuteLabels";

import TransferExecuteInternalBridge from "v3/pages/transfers/TransferExecuteInternalBridge";
import TransferExecuteThirdPartiesBridge from "v3/pages/transfers/TransferExecuteThirdPartiesBridge";
import TransferExecuteToContactBridge from "v3/pages/transfers/TransferExecuteToContactBridge";

const TRANSFER_EXECUTE_MAIN_COL = {
    size: { xs: 12 },
};

const MAX_REQUIRED = "22";
const TRANSFER_GROUPS = {
    Internal: "internal",
    ThirdParties: "thirdParties",
    ToContact: "toContact",
};

function ExecuteTransferV3Page(props) {
    ExecuteTransferV3Page.propTypes = {
        dispatch: func.isRequired,
        match: shape({
            params: shape({
                transferType: string,
            }),
        }).isRequired,
        location: shape({
            pathname: string.isRequired,
        }).isRequired,
        isDesktop: bool.isRequired,
        activeEnvironment: shape({
            permissions: shape({}),
        }).isRequired,
        transferGroup: string.isRequired,
        summary: shape({}),
        lastHrefPathname: string,
    };

    ExecuteTransferV3Page.defaultProps = {
        summary: null,
        lastHrefPathname: "",
    };

    const { dispatch, match, location, isDesktop, activeEnvironment, transferGroup, summary, lastHrefPathname } = props;

    const [agendaVisible, setAgendaVisible] = useState(true);
    const [isTemplatesVisible, setIsTemplatesVisible] = useState(false);

    const transferType = getTransferExecuteTypeFromPath(location.pathname) || match.params.transferType || "internal";

    useEffect(
        () => () => {
            dispatch(templateActions.closeCreateModal());
        },
        [dispatch],
    );

    useEffect(() => {
        const pathname = location?.pathname || "";
        if (!/\/v[23]\/transfer\/[^/]+\/back$/.test(pathname)) {
            return;
        }
        dispatch(transferActions.clearTransferData());
        dispatch(replace(pathname.replace(/\/back$/, "")));
    }, [dispatch, location?.pathname]);

    useEffect(() => {
        const pathType = getTransferExecuteTypeFromPath(location.pathname);
        if (pathType && pathType !== transferGroup) {
            dispatch(transferActions.setTransferGroup(pathType));
        }
    }, [dispatch, location.pathname, transferGroup]);

    const isTicketOrConfirmationPath = useCallback((pathname) => {
        if (!pathname || typeof pathname !== "string") {
            return false;
        }
        return (
            pathname === TRANSFER_TICKET_V3_PATH ||
            pathname === TRANSFER_CONFIRMATION_V3_PATH ||
            pathname.startsWith("/transfer/confirmation") ||
            pathname.startsWith("/transfer/pending") ||
            pathname.startsWith("/transfer/failed") ||
            pathname.startsWith("/transfer/cancelled") ||
            pathname.startsWith("/transfer/scheduled") ||
            pathname === "/transfer/summary"
        );
    }, []);

    const handleBack = useCallback(() => {
        if (summary?.returnToPending && isUxDashboardImprovementsEnabled()) {
            dispatch(push(summary.draftTransactionListGoBackPath || resolveDraftTransactionListGoBack("")));
            return;
        }
        // Inc 9.4: nunca volver al comprobante/confirmación (rompe ciclo ticket ↔ form).
        if (
            lastHrefPathname?.startsWith("/") &&
            lastHrefPathname !== "/" &&
            lastHrefPathname !== location?.pathname &&
            !isTicketOrConfirmationPath(lastHrefPathname)
        ) {
            dispatch(replace(lastHrefPathname));
            return;
        }
        dispatch(push(getTransferHistoricPath()));
    }, [
        dispatch,
        summary?.draftTransactionListGoBackPath,
        summary?.returnToPending,
        lastHrefPathname,
        location?.pathname,
        isTicketOrConfirmationPath,
    ]);

    const handleCreateTemplateClick = useCallback(
        (values) => {
            dispatch(templateActions.createTemplate(values));
        },
        [dispatch],
    );

    const changeTransferGroup = useCallback(
        (newTransferGroup) => {
            dispatch(transferActions.setTransferGroup(newTransferGroup));
            dispatch(transferActions.clearTransferData());
            setAgendaVisible(true);
        },
        [dispatch],
    );

    const isValidCbu = useCallback((value) => {
        const reg = /^\d+$/;
        return reg.test(value) && value.length === parseInt(MAX_REQUIRED, 10);
    }, []);

    const tabOptions = useMemo(() => {
        const perm = activeEnvironment?.permissions || {};
        const options = [];
        if (perm.transferInternal) {
            options.push({ value: "internal", label: getTransferExecuteLabels("tab.internal") });
        }
        if (perm.transferThirdParties) {
            options.push({ value: "thirdParties", label: getTransferExecuteLabels("tab.thirdParties") });
        }
        if (perm.transferToContact && perm.frequentDestination) {
            options.push({ value: "toContact", label: getTransferExecuteLabels("tab.toContact") });
        }
        return options;
    }, [activeEnvironment?.permissions]);

    const handleTransferTypeChange = useCallback(
        (type) => {
            if (type === "internal") {
                changeTransferGroup(TRANSFER_GROUPS.Internal);
            } else if (type === "thirdParties") {
                changeTransferGroup(TRANSFER_GROUPS.ThirdParties);
            } else {
                changeTransferGroup(TRANSFER_GROUPS.ToContact);
            }
            dispatch(replace(getTransferExecutePath(type)));
        },
        [changeTransferGroup, dispatch],
    );

    const transferTypeToggle =
        tabOptions.length > 1 ? (
            <ToggleButtonGroup
                id="transferTypeSelector"
                data-testid={transfersTestIds.execute.typeToggle}
                name="transferType"
                options={tabOptions}
                defaultValue={transferType}
                size="small"
                variant="light"
                hideSelectedCheck
                handleRemoteChange
                onChange={handleTransferTypeChange}
            />
        ) : null;

    const v3ShellTitleKey = useMemo(() => {
        if (transferType === "internal") {
            return getTransferInternalTitleI18nKey({ mobile: true });
        }
        if (transferType === "toContact") {
            return "transfers.form.toContact.title.mb";
        }
        return "transfers.form.thirdParties.title.mb";
    }, [transferType]);

    const v3ShellTemplatesButton = useMemo(
        () => (
            <Button
                data-testid={transfersTestIds.execute.templates}
                variant="text"
                icon="NewspaperAlt"
                iconVariant="outline"
                color="white"
                size="large"
                css={{ padding: 8 }}
                onClick={() => setIsTemplatesVisible(true)}
            />
        ),
        [],
    );

    useEffect(() => {
        if (!isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        dispatch(layoutActions.setScopes(["form"]));
        if (!isDesktop) {
            dispatch(layoutActions.setTitleKey(v3ShellTitleKey));
            const draftBack =
                summary?.returnToPending &&
                (summary?.draftTransactionListGoBackPath || resolveDraftTransactionListGoBack(""));
            const currentPathname = location?.pathname || "";
            const isSafeOrigin =
                lastHrefPathname?.startsWith("/") &&
                lastHrefPathname !== "/" &&
                !lastHrefPathname.startsWith("/v2/transfer") &&
                !lastHrefPathname.startsWith("/v3/transfer") &&
                lastHrefPathname !== currentPathname &&
                lastHrefPathname !== TRANSFER_TICKET_V3_PATH &&
                lastHrefPathname !== TRANSFER_CONFIRMATION_V3_PATH;
            const goBackPath = draftBack || (isSafeOrigin ? lastHrefPathname : getTransferHistoricPath());
            dispatch(layoutActions.setGoBackPath(goBackPath));
            dispatch(layoutActions.setRightContentMobile(v3ShellTemplatesButton));
        }
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [
        dispatch,
        isDesktop,
        v3ShellTitleKey,
        v3ShellTemplatesButton,
        summary?.returnToPending,
        summary?.draftTransactionListGoBackPath,
        lastHrefPathname,
        location?.pathname,
    ]);

    const bridgeCommonProps = {
        dispatch,
        isDesktop,
        onCreateTemplate: handleCreateTemplateClick,
        isTemplatesVisible,
        setIsTemplatesVisible,
        location,
    };

    const renderActiveForm = () => {
        if (transferType === "internal" && activeEnvironment.permissions.transferInternal) {
            return (
                <TransferExecuteInternalBridge
                    {...bridgeCommonProps}
                    transferGroup={transferGroup}
                    agendaVisible={agendaVisible}
                    setAgendaVisible={setAgendaVisible}
                />
            );
        }
        if (transferType === "thirdParties" && activeEnvironment.permissions.transferThirdParties) {
            return (
                <TransferExecuteThirdPartiesBridge
                    {...bridgeCommonProps}
                    transferGroup={transferGroup}
                    isValidCbu={isValidCbu}
                    agendaVisible={agendaVisible}
                    setAgendaVisible={setAgendaVisible}
                />
            );
        }
        if (
            transferType === "toContact" &&
            activeEnvironment.permissions.transferToContact &&
            activeEnvironment.permissions.frequentDestination
        ) {
            return (
                <TransferExecuteToContactBridge
                    {...bridgeCommonProps}
                    transferGroup={transferGroup}
                    isValidCbu={isValidCbu}
                    schedulerButtonVisible={agendaVisible}
                    setSchedulerButtonVisible={setAgendaVisible}
                />
            );
        }
        return <Redirect to={getTransferExecutePath("internal")} />;
    };

    if (!isTransferHistoricV3Enabled()) {
        const legacyPath = location.pathname.replace("/v3/transfer", TRANSFER_EXECUTE_V2_PREFIX);
        return <Redirect to={legacyPath} />;
    }

    return (
        <>
            {isDesktop && (
                <Container fixed>
                    <Box mb={24}>
                        <PageHeader
                            data-testid={transfersTestIds.execute.header}
                            goBack={handleBack}
                            title={getTransferExecuteLabels("pageTitle")}
                            renderOptionsAs="default"
                            rightOptions={[
                                buildTransferExecuteTemplatesHeaderOption(() => setIsTemplatesVisible(true)),
                            ]}
                        />
                    </Box>
                </Container>
            )}
            {isDesktop && transferTypeToggle && (
                <Container layout="form">
                    <Grid>
                        <Col {...TRANSFER_EXECUTE_MAIN_COL} mb={24}>
                            {transferTypeToggle}
                        </Col>
                    </Grid>
                </Container>
            )}
            {renderActiveForm()}
        </>
    );
}

const mapStateToProps = (state) => ({
    transferGroup: transferSelectors.getTransferGroup(state),
    summary: transferSelectors.getSummary(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    lastHrefPathname: statusSelectors.getPathname(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(ExecuteTransferV3Page);
